/* eslint-disable react-hooks/set-state-in-effect */
import { Box, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { type Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";

// Setup timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = "America/Chicago";

export default function DateTimePickerInput({
  id,
  value,
  field,
  onCellUpdate,
  minDate,
  placeholder,
  showOutline = false,
  forceDesktop = false,
}: {
  id: string;
  value: string | null;
  field: string;
  onCellUpdate: (id: string, field: string, value: string) => void;
  minDate?: Date | string;
  placeholder?: string;
  showOutline?: boolean;
  forceDesktop?: boolean;
}) {
  const [date, setDate] = useState<Dayjs | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (value && dayjs(value).isValid()) {
      setDate(dayjs(value));
    } else {
      setDate(null);
    }
  }, [value, id]);

  const handleDateChange = (newValue: Dayjs | null) => {
    setDate(newValue);
  };

  const handleAccept = (newValue: Dayjs | null) => {
    if (!newValue) return;

    const newDateStr = newValue.toISOString();
    if (newDateStr !== value) {
      onCellUpdate(id, field, newDateStr);
    }

    // Close the picker after accepting
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const dataValue =
    date && date.isValid()
      ? dayjs(date).tz(TIMEZONE).format("MM/DD/YYYY HH:mm")
      : "";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        borderRadius: "4px",
        cursor: "pointer",
      }}
      onClick={() => !isOpen && setIsOpen(true)}
      onFocus={() => !isOpen && setIsOpen(true)}
      onKeyDown={(e) => {
        if (e.key === "Tab") {
          setIsOpen(false);
        }
      }}
    >
      {isOpen ? (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            value={date}
            onChange={handleDateChange}
            onClose={handleClose}
            onAccept={handleAccept}
            open={isOpen}
            ampm={false}
            minutesStep={1}
            timeSteps={{ minutes: 1 }}
            closeOnSelect={false}
            format="MMM D, YYYY HH:mm"
            timezone={TIMEZONE}
            desktopModeMediaQuery={
              forceDesktop ? "@media (min-width: 0px)" : undefined
            }
            {...(minDate
              ? dayjs(minDate).isValid()
                ? { minDate: dayjs(minDate) }
                : {}
              : {})}
            slotProps={{
              textField: {
                sx: { width: "100%" },
                size: "small",
                variant: "outlined",
                onClick: () => !isOpen && setIsOpen(true),
              },
              popper: {
                sx: { zIndex: 9999 },
              },
            }}
          />
        </LocalizationProvider>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            minHeight: showOutline ? "40px" : "auto",
            px: showOutline ? 1.75 : 0,
            ...(showOutline && {
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "4px",
              "&:hover": {
                borderColor: "text.primary",
              },
            }),
          }}
        >
          <Typography
            variant="body2"
            title={dataValue}
            fontSize="inherit"
            sx={{ 
              flexGrow: 1,
              color: dataValue ? "text.primary" : "text.secondary",
            }}
            minHeight="21px"
          >
            {dataValue || placeholder || ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
